// @flow
import * as React from 'react';
import {
  Stream,
  StreamContext,
  CTX_ANNOUNCEMENT_STREAM,
  LimitedStreamPlaceholder,
} from 'chat/components/Stream';
import AnnouncementStreamContainer from './AnnouncementStreamContainer';
import EventTracking from 'lib/EventTracking';
import FreeClassLimitsLearnMoreDialog from 'groups/components/FreeClassLimitsLearnMoreDialog';
import Link from 'components/Link';
import ModalTrigger from 'components/ModalTrigger';
import TextWithIcon from 'components/TextWithIcon';

import type { AnnouncementStreamContainerChildrenProps } from './AnnouncementStreamContainer';

type Props = {
  uuid: string,
  onScrollToBottom?: Function,
  bottomLineComposer: React.Element<any>,
  shouldLimitSendFunctionality: boolean,
  groupCode: string,
};

function LimitedSendFunctionalityComposerAlert(props: { groupCode: string }) {
  return (
    <ModalTrigger
      onClick={() => EventTracking.trackKey('free_product_limits.message_thread.learn_more.click')}
      modal={
        <FreeClassLimitsLearnMoreDialog
          peopleListUrl={`/classes/${props.groupCode}/people`}
          onRequestHide={() => {}}
          uiContext="thread"
        />
      }>
      {/* TODO: refactor ComposerAlert so that presentation is decoupled
      from data, and use the presentational component here */}
      <div className="ComposerAlert ComposerAlert-repliesDisabled">
        <TextWithIcon gutter="small" icon="megaphone-off" iconColor="thunder" inline>
          You cannot send announcements to this class. <Link weight="semibold">Learn more</Link>
        </TextWithIcon>
      </div>
    </ModalTrigger>
  );
}

class AnnouncementStream extends React.Component<Props> {
  stream: Stream;

  handleNewBottomItem = () => {
    if (this.stream) {
      this.stream.scrollToBottom();
    }
  };

  renderStream = (propsFromContainer: AnnouncementStreamContainerChildrenProps) => {
    const { shouldLimitSendFunctionality, groupCode } = this.props;
    const streamComponent = (
      <Stream
        {...propsFromContainer}
        uuid={this.props.uuid}
        ref={(e: any) => (this.stream = e)}
        onScrollToBottom={this.props.onScrollToBottom}
      />
    );

    if (!shouldLimitSendFunctionality) {
      // return the stream and composer normally if there are no
      // limits on send functionality
      return (
        <React.Fragment>
          {streamComponent}
          {this.props.bottomLineComposer}
        </React.Fragment>
      );
    }

    if (propsFromContainer.hasItems) {
      // return the stream and a disabled composer notice if there are
      // existing messages + limits on send functionality
      return (
        <React.Fragment>
          {streamComponent}
          <LimitedSendFunctionalityComposerAlert groupCode={groupCode} />
        </React.Fragment>
      );
    }

    // return a null state if there are no existing messages
    // + limits on send functionality
    return <LimitedStreamPlaceholder peopleListUrl={`/classes/${groupCode}/people`} />;
  };

  render() {
    return (
      <StreamContext.Provider value={CTX_ANNOUNCEMENT_STREAM}>
        <AnnouncementStreamContainer
          uuid={this.props.uuid}
          onNewBottomItem={this.handleNewBottomItem}>
          {propsFromContainer => this.renderStream(propsFromContainer)}
        </AnnouncementStreamContainer>
      </StreamContext.Provider>
    );
  }
}

export default AnnouncementStream;
